import { useMemo } from "react";
import {
  createAllergyMatcher,
  mergeAllergenDeclarations,
  type AllergenDeclaration,
  type AllergyMatcher,
  type AllergyPolicy
} from "@kanpla/allergens";

import { useUser } from "~/api/users/hooks/use-user";
import {
  type CollectTill,
  useCollectTillContext
} from "~/routes/__app.collect/collect-till-context";

type CollectionModule = CollectTill["config"]["collectionModules"][number];

/** A module the till doesn't serve has no policy to follow, so a match refuses. */
const UNKNOWN_MODULE_POLICY: AllergyPolicy = {
  contains: "block",
  mayContain: "block"
};

export type AllergyMatcherForModule = (moduleId: string) => AllergyMatcher;

export function createModuleAllergyMatchers(
  childAllergens: readonly string[],
  modules: readonly Pick<CollectionModule, "id" | "allergyPolicy">[]
): AllergyMatcherForModule {
  const matchers = new Map(
    modules.map((module) => [
      module.id,
      createAllergyMatcher(childAllergens, module.allergyPolicy)
    ])
  );
  const unknownModule = createAllergyMatcher(
    childAllergens,
    UNKNOWN_MODULE_POLICY
  );

  return (moduleId) => matchers.get(moduleId) ?? unknownModule;
}

/** The child's allergy matcher for each of the till's collection modules. */
export function useCollectAllergyMatchers(
  childId: string | null
): AllergyMatcherForModule {
  const { data: user } = useUser({ childId });
  const modules = useCollectTillContext(
    (till) => till.config.collectionModules
  );
  const allergens = user?.allergens;

  return useMemo(
    () => createModuleAllergyMatchers(allergens ?? [], modules),
    [allergens, modules]
  );
}

/** The child's allergy matcher for one collection module. */
export function useCollectAllergyMatcher({
  childId,
  moduleId
}: {
  childId: string;
  moduleId: string;
}): AllergyMatcher {
  return useCollectAllergyMatchers(childId)(moduleId);
}

/**
 * The collect endpoints' flat allergen lists mix confirmed and "may contain"
 * keys, so they are declared as `contains`.
 */
export function collectLineDeclaration(
  productAllergens: readonly string[],
  options: readonly { allergens: readonly string[] }[]
): AllergenDeclaration {
  return mergeAllergenDeclarations(
    { contains: productAllergens, mayContain: [] },
    ...options.map(({ allergens }) => ({ contains: allergens, mayContain: [] }))
  );
}
